import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { Translate } from 'react-localize-redux';
import AppState from '@webapp/store/state';
import styled from 'styled-components';

// Types
import { RecordLabel } from '@common/types/record-label/record-label';

// Store
import { GetRecordLabels } from '@webapp/store/record-labels/action-creators';

// Components
import Box from '@components/layout/box';
import Avatar from '@components/basics/avatar';
import PlusIcon from '@components/basics/icons/plus';
import BoxHeader from '@components/layout/box-header';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import Flex from '../../../components/layout/flex';
import Tooltip from '@components/basics/tooltip';
import ArrowIcon from '@components/basics/icons/arrow';

// Controllers
import EditLabelProfileMenu from '../../menus/edit-label-profile-menu';
import { AnimatePresence } from 'framer-motion';
import { ListReleasesDeactivatedArtist } from '@webapp/store/release/action-creators';
import { ListDeactivatedArtistEarnings } from '@webapp/store/earnings/action-creators';
import SquareAvatar from '../../../components/basics/square-avatar';
import { RecordLabelRelation } from '@services/record-labels/types/record-label-relation';

interface LabelEditorCtrlProps {
  className?: string;
  activeLabel?: RecordLabel;
  relations: RecordLabelRelation[];
  isMobile?: boolean;
  getRecordLabels: () => GetRecordLabels;
  createNewArtist: () => CallHistoryMethodAction;
  goToRestoreDeactivatedArtistPage: (
    artistId: string
  ) => CallHistoryMethodAction;
  getReleasesDeactivatedArtist: (
    artistId: string
  ) => ListReleasesDeactivatedArtist;
  getEarningsDeactivatedArtist: (
    artistId: string
  ) => ListDeactivatedArtistEarnings;
}

const mapStateToProps = (state: AppState) => {
  const activeLabel = state.recordLabels.active;
  return {
    isMobile: state.ui.mobile,
    activeLabel: activeLabel,
    relations: state.recordLabels.artists[activeLabel?.id || ''] || [],
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getRecordLabels: () => dispatch(new GetRecordLabels()),
  createNewArtist: () => dispatch(push('/artist/new')),
  goToRestoreDeactivatedArtistPage: (artistId: string) =>
    dispatch(push(`/restore/artist/?id=${artistId}`)),
  getReleasesDeactivatedArtist: (artistId: string) =>
    dispatch(new ListReleasesDeactivatedArtist(artistId)),
  getEarningsDeactivatedArtist: (artistId: string) =>
    dispatch(new ListDeactivatedArtistEarnings(artistId)),
});

export const LabelEditorCtrl: React.FC<LabelEditorCtrlProps> = (props) => {
  const [showTooltip, setShowTooltip] = React.useState<string>('');
  const [showDeactivated, setShowDeactivated] = React.useState<boolean>(false);

  const handleAddConnectedArtist = () => {
    props.createNewArtist();
  };

  const handleAddDeactivatedArtist = (artist: RecordLabelRelation) => {
    props.getReleasesDeactivatedArtist(artist.artistId);
    props.getEarningsDeactivatedArtist(artist.artistId);
    props.goToRestoreDeactivatedArtistPage(artist.artistId);
  };

  if (!props.activeLabel) {
    return null;
  }

  return (
    <MotionOpacity className={props.className}>
      {!props.isMobile && (
        <Typography type="subheader" customFont="semi-bold" className="mb-30">
          <Translate id="LABEL_PROFILE.SETTING.TITLE" />
        </Typography>
      )}
      <GridWrapper className="has-margin">
        <Box noBorderBottom>
          <AvatarContainer>
            <SquareAvatar
              src={props.activeLabel.avatar}
              username={props.activeLabel.name}
            />
            <div>
              <Typography
                className="mb-5"
                type="big-title"
                customFont="semi-bold"
              >
                {props.activeLabel.name}
              </Typography>
              <EditLabelProfileMenu />
            </div>
          </AvatarContainer>
        </Box>

        <Box
          noBorderBottom={
            props.relations?.filter((a) => a.isDeleted).length > 0
          }
          noBorderTop
        >
          <BoxHeader
            title={
              <Translate
                id="USER_PROFILE.ROSTER.CONNECTED.TITLE"
                data={{
                  amount: props.relations?.filter((a) => !a.isDeleted).length,
                }}
              />
            }
            text={<Translate id="USER_PROFILE.ROSTER.CONNECTED.TEXT" />}
          />
          <AvatarList>
            {props.relations
              .filter((a) => !a.isDeleted)
              ?.map((artist) => (
                <div
                  key={artist.artistId}
                  id={artist.artistId}
                  onMouseEnter={() => setShowTooltip(artist.artistName)}
                  onMouseLeave={() => setShowTooltip('')}
                  onTouchStart={() => setShowTooltip(artist.artistName)}
                  onTouchEnd={() => setTimeout(() => setShowTooltip(''), 2000)}
                >
                  <StyledAvatar
                    key={artist.id}
                    alt={artist.artistName}
                    src={artist.artistAvatar}
                    size="48"
                  />
                  <TooltipWrapper>
                    <Tooltip
                      isOpen={
                        !!artist.artistName && artist.artistName === showTooltip
                      }
                      centerPointer
                      minWidth="100px"
                    >
                      {artist.artistName}
                    </Tooltip>
                  </TooltipWrapper>
                </div>
              ))}
            <AddButton onClick={handleAddConnectedArtist}>
              <PlusIcon className="plus-icon" noAnimation />
            </AddButton>
          </AvatarList>
        </Box>
        {props.relations?.filter((a) => a.isDeleted).length ? (
          <Box noBorderTop>
            <BoxHeader
              title={
                <Translate
                  id="USER_PROFILE.ROSTER.DEACTIVATED.TITLE"
                  data={{
                    amount: props.relations?.filter((a) => a.isDeleted).length,
                  }}
                />
              }
              text={<Translate id="USER_PROFILE.ROSTER.DESACTIVATED.TEXT" />}
              noMargin={true}
              customAction={
                <ArrowWrapper
                  onClick={() => setShowDeactivated(!showDeactivated)}
                >
                  <ArrowIcon color="dark" up={!showDeactivated} />
                </ArrowWrapper>
              }
            />
            <AnimatePresence exitBeforeEnter>
              {showDeactivated && (
                <MotionOpacity>
                  <AvatarList>
                    {props.relations
                      .filter((a) => a.isDeleted)
                      ?.map((artist) => (
                        <div
                          key={artist.artistId}
                          id={artist.artistId}
                          onMouseEnter={() => setShowTooltip(artist.artistName)}
                          onMouseLeave={() => setShowTooltip('')}
                          onTouchStart={() => setShowTooltip(artist.artistName)}
                          onTouchEnd={() =>
                            setTimeout(() => setShowTooltip(''), 2000)
                          }
                        >
                          <StyledAvatar
                            className="deactivated"
                            key={artist.id}
                            alt={artist.artistName}
                            src={artist.artistAvatar}
                            size="48"
                            onClick={() => handleAddDeactivatedArtist(artist)}
                          />
                          <TooltipWrapper>
                            <Tooltip
                              isOpen={
                                !!artist.artistName &&
                                artist.artistName === showTooltip
                              }
                              centerPointer
                              minWidth="100px"
                            >
                              {artist.artistName}
                            </Tooltip>
                          </TooltipWrapper>
                        </div>
                      ))}
                  </AvatarList>
                </MotionOpacity>
              )}
            </AnimatePresence>
          </Box>
        ) : null}
      </GridWrapper>
    </MotionOpacity>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(LabelEditorCtrl);

const GridWrapper = styled.div`
  display: grid;
  grid-gap: 3px;
  &.has-margin {
    margin-bottom: 20px;
  }
`;

const TooltipWrapper = styled.div`
  position: relative;
`;

const AvatarList = styled.div`
  width: 100%;
  display: grid;
  grid-gap: 10px;
  align-items: center;
  justify-items: center;
  grid-template-columns: repeat(4, minmax(48px, 1fr));

  ${({ theme }) => `@media screen and ${theme.device.laptopL}`} {
    grid-template-columns: repeat(8, minmax(48px, 1fr));
  }
`;

const AvatarContainer = styled(Flex)`
  gap: 30px;
  align-items: center;
`;

const AddButton = styled.button`
  cursor: pointer;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.accent};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  .plus-icon {
    width: 12px;
    height: 12px;
    path {
      fill: ${({ theme }) => theme.colors.white};
    }
  }

  &:hover,
  &:active {
    background-color: ${({ theme }) => theme.colors.accentHover};
  }

  &.disabled {
    pointer-events: none;
    background-color: ${({ theme }) => theme.colors.secondary};

    .plus-icon {
      path {
        fill: ${({ theme }) => theme.colors.tertiary};
      }
    }
  }
`;

const StyledAvatar = styled(Avatar)`
  width: 48px;
  height: 48px;

  &.deactivated {
    opacity: 0.5;
    cursor: pointer;
    overflow: visible;
    transition: opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:after {
      content: '';
      border-radius: 50%;
      position: absolute;
      inset: -3px;
      opacity: 0.4;
      border: 2px dashed ${({ theme }) => theme.colors.primary};
    }

    &:hover {
      opacity: 0.9;
    }
  }
`;

const ArrowWrapper = styled.div`
  cursor: pointer;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.white};
`;
